import { Dialog, DialogTitle, DialogContent, TextField, Button, IconButton, Collapse } from '@mui/material'
import { useEffect, useState } from 'react'
import { usersRoutes, authRoutes } from '../routes'
import { useAuth, useStatus } from '../context'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'

const UserDataEditDialog = ({ open, handleClose, userData }) => {
    const { setUserData } = useAuth()
    const { setstatus } = useStatus()
    const [nombre, setNombre] = useState('')
    const [apellido, setApellido] = useState('')
    const [email, setEmail] = useState('')
    const [telefono, setTelefono] = useState('')

    const [emailError, setEmailError] = useState(false)
    const [emailHelperText, setEmailHelperText] = useState('')
    const [telefonoError, setTelefonoError] = useState(false)
    const [telefonoHelperText, setTelefonoHelperText] = useState('')

    const [oldPassword, setOldPassword] = useState('')
    const [password, setPassword] = useState('')
    const [confirmePassword, setConfirmePassword] = useState('')
    const [tipoOldPassword, setTipoOldPassword] = useState('password')
    const [tipoPassword, setTipoPassword] = useState('password')
    const [tipoConfirmePassword, setTipoConfirmePassword] = useState('password')
    const [showChangePassword, setShowChangePassword] = useState(false)

    useEffect(() => {
        if (userData) {
            setNombre(userData.name || '')
            setApellido(userData.lastName || '')
            setEmail((userData.email === 'x@x.xx') ? '' : userData.email || '')
            setTelefono(userData.phone || '')
            setEmailError(false)
            setEmailHelperText('')
            setTelefonoError(false)
            setTelefonoHelperText('')
            setOldPassword('')
            setPassword('')
            setConfirmePassword('')
            setShowChangePassword(false)
        }
    }, [userData, open])

    const validateEmail = (val) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!val) {
            setEmailError(true)
            setEmailHelperText('El correo electrónico es requerido')
            return false
        }
        if (!emailRegex.test(val)) {
            setEmailError(true)
            setEmailHelperText('Ingrese un formato de correo válido (ej: usuario@gmail.com)')
            return false
        }
        if (val.length > 100) {
            setEmailError(true)
            setEmailHelperText('El correo no puede superar los 100 caracteres')
            return false
        }
        setEmailError(false)
        setEmailHelperText('')
        return true
    }

    const handlePhoneChange = (val) => {
        let cleaned = ''
        for (let i = 0; i < val.length && i < 12; i++) {
            const char = val[i]
            if (i === 0 && (char === '+' || /[0-9]/.test(char))) {
                cleaned += char
            } else if (i > 0 && /[0-9]/.test(char)) {
                cleaned += char
            }
        }
        setTelefono(cleaned)
        setTelefonoError(false)
        setTelefonoHelperText('')
    }

    const validateTelefono = () => {
        setTelefonoError(false)
        setTelefonoHelperText('')
        return true
    }

    const saveUser = async () => {
        const isEmailValid = validateEmail(email)

        if (!isEmailValid) {
            return
        }

        let passwordChanged = false

        // Check if user is trying to change password
        if (oldPassword || password || confirmePassword) {
            if (!oldPassword || !password || !confirmePassword) {
                return setstatus({
                    status: 'error',
                    title: 'Campos Incompletos',
                    message: 'Por favor, complete todos los campos para cambiar la contraseña.',
                    severity: 'error'
                })
            }
            if (password.length < 8) {
                return setstatus({
                    status: 'error',
                    title: 'Contraseña Corta',
                    message: 'La nueva contraseña debe tener al menos 8 caracteres.',
                    severity: 'error'
                })
            }
            if (password !== confirmePassword) {
                return setstatus({
                    status: 'error',
                    title: 'Contraseñas No Coinciden',
                    message: 'Las nuevas contraseñas no coinciden.',
                    severity: 'error'
                })
            }
            try {
                await authRoutes.cambiarPassword(oldPassword, password)
                passwordChanged = true
            } catch (error) {
                console.error('Error changing password:', error)
                return setstatus({
                    status: 'error',
                    title: 'Error al Cambiar Contraseña',
                    message: error.response?.data?.message || 'Hubo un error al cambiar la contraseña.',
                    severity: 'error'
                })
            }
        }

        if (compareChanges()) {
            try {
                const response = await usersRoutes.updateProfile({
                    name: nombre,
                    lastName: apellido,
                    email: email,
                    phone: telefono
                })
                if (response && response.data) {
                    setUserData(response.data)
                    handleClose()
                    setstatus({
                        status: 'completed',
                        title: 'Perfil Actualizado',
                        message: passwordChanged
                            ? 'Datos personales y contraseña actualizados correctamente.'
                            : 'Datos personales actualizados correctamente.',
                        severity: 'success'
                    })
                }
            } catch (error) {
                console.error('Error updating profile:', error)
                const errorMsg = error.response?.data?.message || 'Ocurrió un error al actualizar el perfil.'
                setstatus({
                    status: 'error',
                    title: 'Error al Actualizar Perfil',
                    message: errorMsg,
                    severity: 'error'
                })
            }
        } else {
            if (passwordChanged) {
                handleClose()
                setstatus({
                    status: 'completed',
                    title: 'Contraseña Actualizada',
                    message: 'Contraseña actualizada correctamente.',
                    severity: 'success'
                })
            } else {
                setstatus({
                    status: 'completed',
                    title: 'Sin Cambios',
                    message: 'No se realizaron cambios en los datos personales.',
                    severity: 'info'
                })
            }
        }
    }

    const compareChanges = () => {
        if (userData.name !== nombre) {
            return true
        } else if (userData.lastName !== apellido) {
            return true
        } else if (userData.email !== email) {
            return true
        } else if (userData.phone !== telefono) {
            return true
        } else {
            return false
        }
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
        >
            <div style={{ minWidth: 320 }}>
                <DialogTitle>Editar mis datos</DialogTitle>
                <DialogContent style={{ width: '100%' }}>
                     <br />
                    <TextField 
                        onChange={(e) => { 
                            setEmail(e.target.value)
                            validateEmail(e.target.value)
                        }} 
                        error={emailError}
                        helperText={emailHelperText}
                        inputProps={{ maxLength: 100 }}
                        style={{ marginBottom: 20, width: '100%' }} 
                        id="outlined-basic" 
                        label="Email" 
                        variant="outlined" 
                        value={email} 
                    />
                    <br />
                    <TextField 
                        onChange={(e) => handlePhoneChange(e.target.value)} 
                        error={telefonoError}
                        helperText={telefonoHelperText}
                        inputProps={{ maxLength: 12 }}
                        placeholder="+56911223344"
                        style={{ marginBottom: 20, width: '100%' }} 
                        id="outlined-basic" 
                        label="Teléfono" 
                        variant="outlined" 
                        value={telefono} 
                    />
                    
                    <div style={{ textAlign: 'left', marginTop: 10, marginBottom: 10 }}>
                        <Button 
                            variant="text" 
                            size="small" 
                            onClick={() => {
                                if (showChangePassword) {
                                    setOldPassword('')
                                    setPassword('')
                                    setConfirmePassword('')
                                }
                                setShowChangePassword(!showChangePassword)
                            }}
                            style={{ textTransform: 'none', padding: 0 }}
                        >
                            {showChangePassword ? 'Cancelar cambio de contraseña' : 'Cambiar contraseña'}
                        </Button>
                    </div>

                    <Collapse in={showChangePassword}>
                        <hr style={{ margin: '10px 0 20px 0', borderColor: '#eee', borderStyle: 'solid', borderWidth: '0.5px' }} />
                        <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#555' }}>Cambiar Contraseña</h3>
                        
                        <div style={{ position: 'relative', marginBottom: '20px' }}>
                            <TextField
                                type={tipoOldPassword}
                                size={'small'}
                                onChange={(e) => setOldPassword(e.target.value)}
                                style={{ width: '100%' }}
                                label="Contraseña Actual"
                                variant="outlined"
                                value={oldPassword}
                            />
                            <IconButton style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)' }} onClick={() => setTipoOldPassword(tipoOldPassword === 'password' ? 'text' : 'password')}>
                                <FontAwesomeIcon icon={tipoOldPassword === 'password' ? faEye : faEyeSlash} />
                            </IconButton>
                        </div>
                        
                        <div style={{ position: 'relative', marginBottom: '20px' }}>
                            <TextField
                                type={tipoPassword}
                                size={'small'}
                                onChange={(e) => setPassword(e.target.value)}
                                style={{ width: '100%' }}
                                label="Nueva Contraseña"
                                variant="outlined"
                                value={password}
                            />
                            <IconButton style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)' }} onClick={() => setTipoPassword(tipoPassword === 'password' ? 'text' : 'password')}>
                                <FontAwesomeIcon icon={tipoPassword === 'password' ? faEye : faEyeSlash} />
                            </IconButton>
                        </div>
                        
                        <div style={{ position: 'relative', marginBottom: '20px' }}>
                            <TextField
                                type={tipoConfirmePassword}
                                size={'small'}
                                onChange={(e) => setConfirmePassword(e.target.value)}
                                style={{ width: '100%' }}
                                label="Confirmar Nueva Contraseña"
                                variant="outlined"
                                value={confirmePassword}
                            />
                            <IconButton style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)' }} onClick={() => setTipoConfirmePassword(tipoConfirmePassword === 'password' ? 'text' : 'password')}>
                                <FontAwesomeIcon icon={tipoConfirmePassword === 'password' ? faEye : faEyeSlash} />
                            </IconButton>
                        </div>
                    </Collapse>
                    
                    <br />
                    <Button style={{ background: 'brown', color: 'white', marginBottom: 15, width: '100%' }} fullWidth onClick={saveUser}>
                        Guardar
                    </Button>
                    <Button style={{ background: 'red', color: 'white' }} fullWidth onClick={handleClose}>
                        Cancelar
                    </Button>
                </DialogContent>
            </div>
        </Dialog>
    )
}

export default UserDataEditDialog
